import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Download, FileUp, Upload } from "lucide-react";
import { bulkImportQuestions } from "../../redux/slices/questionSlice";
import { selectFlatCategories } from "../../redux/slices/categorySlice";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../components/ui/use-toast";
import {
  csvRowToQuestion,
  downloadCsv,
  getSampleQuestionsCsv,
  parseCsv,
} from "../../lib/questions-csv";

function BulkImportQuestionsDialog({ open, onOpenChange, onSuccess }) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const flatCategories = useSelector(selectFlatCategories);
  const fileInputRef = useRef(null);
  const [categoryId, setCategoryId] = useState("");
  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);

  const resetFile = () => {
    setFileName("");
    setCsvText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadSample = () => {
    downloadCsv("questions-sample.csv", getSampleQuestionsCsv());
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && file.type && !file.type.includes("csv")) {
      toast({
        title: "Invalid file",
        description: "Please upload a .csv file.",
        variant: "destructive",
      });
      resetFile();
      return;
    }
    try {
      const text = await file.text();
      setCsvText(text);
      setFileName(file.name);
    } catch {
      toast({
        title: "Could not read file",
        description: "Try another CSV file.",
        variant: "destructive",
      });
      resetFile();
    }
  };

  const handleImport = async () => {
    if (!categoryId) {
      toast({
        title: "Category required",
        description: "Select a category for these questions.",
        variant: "destructive",
      });
      return;
    }
    if (!csvText.trim()) {
      toast({
        title: "No CSV selected",
        description: "Upload a CSV file or download the sample first.",
        variant: "destructive",
      });
      return;
    }

    let questions;
    try {
      const { rows } = parseCsv(csvText);
      if (!rows.length) {
        throw new Error("CSV has no data rows");
      }
      questions = rows.map((row, i) => csvRowToQuestion(row, i));
    } catch (err) {
      toast({
        title: "Invalid CSV",
        description: err?.message || "Could not parse the CSV file.",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    try {
      const result = await dispatch(
        bulkImportQuestions({
          questions: questions.map((q) => ({
            ...q,
            category_id: categoryId,
          })),
          category_id: categoryId,
        })
      ).unwrap();
      const created =
        result?.created_count ?? result?.created?.length ?? 0;
      const errors = result?.error_count ?? 0;
      if (created === 0) {
        toast({
          title: "Import failed",
          description:
            result?.errors?.[0]?.error ||
            result?.errors?.[0]?.message ||
            "No questions were created. Check required columns.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Import complete",
        description: `Created ${created} question(s)${errors ? `, ${errors} failed` : ""}.`,
      });
      resetFile();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast({
        title: "Import failed",
        description: err || "Could not import questions",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetFile();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk import questions</DialogTitle>
          <DialogDescription>
            Upload a CSV file. Download the sample for the exact column layout.
            <code className="mx-1">correct_answers</code> uses 0-based option
            indexes (e.g. <code>0</code> or <code>0,2</code>).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Assign to category *</Label>
            <Select value={categoryId || undefined} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select category for these questions" />
              </SelectTrigger>
              <SelectContent>
                {flatCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {"\u00A0".repeat((c.level || 0) * 2)}
                    {c.level > 0 ? "└ " : ""}
                    {c.name?.en || c.name || c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={handleDownloadSample}>
              <Download className="mr-2 h-4 w-4" />
              Sample CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUp className="mr-2 h-4 w-4" />
              Choose CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {fileName ? (
            <p className="text-sm text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{fileName}</span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No file selected yet.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || !categoryId || !csvText}
          >
            <Upload className="mr-2 h-4 w-4" />
            {importing ? "Importing…" : "Import CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkImportQuestionsDialog;
